<template>
  <div>
    <div
      :class="{
        [`${el}__sticky`]: true,
        [`${el}__sticky--show`]: stickyDesktopHead.length > 0,
      }"
    >
      <Container>
        <div :class="`${el}__stickyInner`">
          <Row verticalCenter>
            <Col :xs="4">
              {{
                $t('careLevelCalculator.wizard.module.done', {
                  current: currentQuestion,
                  max: maxQuestions,
                })
              }}
            </Col>
            <Col :xs="8">
              <Row>
                <Col
                  v-for="(stickyItem, stickyIndex) in stickyDesktopHead"
                  :key="`item-${stickyIndex}`"
                  autoWidth
                  center
                >
                  <Copy :staticClass="`${el}__stepItemHeadCol`" element="div" bold>
                    {{ stickyItem }}
                  </Copy>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Container>
    </div>

    <InViewAnimation>
      <template v-for="(item, index) in wizard[currentModule].questions">
        <template :slot="index + 1">
          <Row
            :key="`careLevelStepHead-${index}`"
            :ref="`head-${index}`"
            :class="`${el}__stepItemHead`"
            v-if="
              index === 0 ||
              JSON.stringify(item.answers) !==
                JSON.stringify(wizard[currentModule].questions[index - 1].answers)
            "
            verticalCenter
          >
            <Col :xs="4">
              <template v-if="index === 0">
                {{
                  $t('careLevelCalculator.wizard.module.done', {
                    current: currentQuestion,
                    max: maxQuestions,
                  })
                }}
              </template>
            </Col>
            <Col :xs="8">
              <Row>
                <Col
                  v-for="(answerItem, answerIndex) in item.answers"
                  :key="`answer-${answerIndex}`"
                  autoWidth
                  center
                >
                  <Copy :staticClass="`${el}__stepItemHeadCol`" element="div" bold>
                    {{ answerItem }}
                  </Copy>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row
            :key="`careLevelStep-${index}`"
            :class="{
              [`${el}__stepItemRow`]: true,
              [`${el}__stepItemRow--error`]: isError(index),
              [`${el}__stepItemRow--current`]: currentQuestionRow === index,
            }"
            verticalCenter
          >
            <Col :xs="4">
              <Copy element="div" :staticClass="`${el}__questionHeadline`" color="primaryBlue" bold>
                {{ item.question }}

                <Icon
                  v-if="item.info"
                  :class="`${el}__questionInfo`"
                  name="information-line"
                  color="primaryBlue"
                  :content="item.info"
                  v-tippy="{ arrow: true }"
                  isButton
                />
              </Copy>
            </Col>

            <Col :xs="8">
              <Row>
                <Col
                  v-for="(answerItem, answerIndex) in item.answers"
                  :key="`baseRadio-${currentModule}-${answerIndex}`"
                  autoWidth
                  center
                >
                  <template v-if="answerIndex !== 1 || !item.hideDayOption">
                    <template
                      v-if="
                        item.withInputCounter &&
                        activeWizardAnswers[currentModule][index] === answerIndex &&
                        activeWizardAnswers[currentModule][index] > 0
                      "
                    >
                      <InputCounter
                        :class="`${el}__inputCounterDesktop`"
                        :defaultValue="activeCounterInputValues[index] || 1"
                        @change="(value) => $emit('onInputCounterChange', index, value)"
                      />
                    </template>
                    <BaseRadio
                      v-else
                      :id="`question-${currentModule}-${index}-${answerIndex}`"
                      :name="`question-${currentModule}-${index}`"
                      checkmark
                      advanced
                      :checked="activeWizardAnswers[currentModule][index] === answerIndex"
                      @input="$emit('change', index, answerIndex)"
                      tabindex="0"
                      @keyup.enter.native="$emit('change', index, answerIndex)"
                      @keyup.space.native="$emit('change', index, answerIndex)"
                      role="radio"
                      :aria-checked="activeWizardAnswers[currentModule][index] === answerIndex"
                    />
                  </template>
                </Col>
              </Row>
            </Col>
          </Row>
        </template>
      </template>
    </InViewAnimation>

    <transition name="fade">
      <div v-if="showToast" :class="`${el}__toast`">
        <Icon name="information-fill" color="supportDanger" />
        {{ $t('careLevelCalculator.wizard.moduleOverview.error') }}
      </div>
    </transition>
  </div>
</template>

<script>
import Container from '@/objects/Container';
import Copy from '@/objects/Copy';
import Icon from '@/objects/Icon';
import BaseRadio from '@/objects/Forms/BaseRadio';
import InputCounter from '@/objects/InputCounter';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';
import InViewAnimation from '@/components/InViewAnimation';

import { scrollTo } from '@/helpers/scroll';

import { mapFieldsCareLevelCalculator, mapFieldsAuth } from '@/store/mapFields';

export default {
  name: 'CareLevelCalculatorWizardDesktop',
  props: {
    wizard: Array,
    el: String,
    currentModule: Number,
    valid: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    Container,
    Copy,
    Icon,
    BaseRadio,
    InputCounter,
    Row,
    Col,
    InViewAnimation,
  },
  watch: {
    valid() {
      if (!this.valid) {
        setTimeout(() => {
          const element = document.querySelectorAll(`.${this.el}__stepItemRow--error`)[0];
          scrollTo(element, -160);
        }, 100);

        this.showToast = true;
        setTimeout(() => {
          this.showToast = false;
        }, 6000);
      }
    },
  },
  computed: {
    ...mapFieldsCareLevelCalculator(['activeWizardAnswers', 'activeCounterInputValues']),
    ...mapFieldsAuth(['isAuthenticated']),

    maxQuestions() {
      return this.wizard[this.currentModule].questions.length;
    },

    currentQuestion() {
      return this.activeWizardAnswers[this.currentModule].filter((c) => c !== null).length;
    },

    currentQuestionRow() {
      return this.activeWizardAnswers[this.currentModule].indexOf(null);
    },
  },
  data() {
    return {
      stickyDesktopHead: [],
      showToast: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    isError(index) {
      return this.activeWizardAnswers[this.currentModule][index] === null && !this.valid;
    },

    onScroll() {
      if (!this.$matchMedia.l) {
        return false;
      }

      /**
       * I'm not so happy with that :/
       */
      const offset = this.isAuthenticated ? 80 : 14;
      let activeIdx = null;

      const { top } = this.$refs['head-0'][0].$el.getBoundingClientRect();
      if (top - offset > 0 && this.stickyDesktopHead.length > 0) {
        this.stickyDesktopHead = [];
      }

      Object.keys(this.$refs).map((r) => {
        const { top } = this.$refs[r][0].$el.getBoundingClientRect();
        if (top < offset) {
          activeIdx = r.replace('head-', '');
        }
      });

      if (
        activeIdx &&
        JSON.stringify(this.stickyDesktopHead) !==
          JSON.stringify(this.wizard[this.currentModule].questions[activeIdx].answers)
      ) {
        this.stickyDesktopHead = this.wizard[this.currentModule].questions[activeIdx].answers;
      }
    },
  },
};
</script>
